import { Flag, Footprints, Inbox } from 'lucide-react';

import { localDay } from '../../shared/calendar';
import type { LocalRecord, Task } from '../../shared/model';
import type { ReturnSummary } from '../../shared/returning';

import { capitalised, clockTime, duration, numberWord, sinceDay, weekday } from '../lib/day-words';
import { taskStone } from '../lib/stones';
import { TaskBadges, taskReason } from './StatusBadge';
import { GhostStone, MarkerNote, StoneMark } from './Stones';
import { Button, QuietButton } from './ui';

/** The quiet days, said plainly and without a count to beat. */
export function trailWords(quiet: string[]) {
  if (quiet.length === 0) {
    return 'Nothing piled up.';
  }

  if (quiet.length === 1) {
    return `${weekday(quiet[0])} is just trail. Nothing piled up.`;
  }

  if (quiet.length === 2) {
    return `${weekday(quiet[0])} and ${weekday(quiet[1])} are just trail. Nothing piled up.`;
  }

  return `${capitalised(numberWord(quiet.length))} days of trail. Nothing piled up.`;
}

/**
 * The top of Today on the first morning after days away, as the phone shows
 * it. It says three things and asks for nothing: where the owner stopped, with
 * a way back in that opens the task without starting the clock; what came
 * due, and who is waiting; and that the days in between are just trail.
 */
export function WelcomeBack({
  summary,
  records,
  day,
  onOpen,
  onSetOut,
  onDismiss,
}: {
  summary: ReturnSummary;
  records: LocalRecord[];
  day: string;
  onOpen: (id: string) => void;
  onSetOut: () => void;
  onDismiss: () => void;
}) {
  const byId = new Map(records.filter(r => r.kind === 'task').map(r => [r.id, r]));
  const marked = summary.marker && byId.get(summary.marker.taskId);

  const rows = [...summary.due.slice(0, 2), ...summary.waiting.slice(0, 2)]
    .map(id => byId.get(id))
    .filter((r): r is LocalRecord => Boolean(r))
    .map(record => ({ record, reason: taskReason(record.value as Task, record.id, records, day) }));

  const count = rows.length + (summary.inbox ? 1 : 0);

  return (
    <section className="welcome-back" aria-label="Welcome back">
      {summary.marker && marked && (
        <article className="welcome-marker">
          <span className="welcome-marker-label">
            <Flag aria-hidden="true" />
            {summary.marker.leftAt
              ? `You left a marker on ${sinceDay(localDay(new Date(summary.marker.leftAt)), day)}`
              : 'You left a marker'}
          </span>
          <div>
            <h2>{(marked.value as Task).title}</h2>
            <TaskBadges record={marked} records={records} />
          </div>
          <MarkerNote
            text={summary.marker.text}
            when={
              summary.marker.leftAt
                ? `${sinceDay(localDay(new Date(summary.marker.leftAt)), day)} ${clockTime(summary.marker.leftAt)}`
                : undefined
            }
          />
          <Progress record={marked} minutes={summary.marker.minutes} />
          <div className="welcome-pick-up">
            <Button variant="primary" onClick={() => onOpen(marked.id)}>
              Pick up where you left off →
            </Button>
            <span className="small-muted">Opening the task doesn’t start the clock.</span>
          </div>
        </article>
      )}
      <div className="section-heading today-section-heading">
        <h2>Since {sinceDay(summary.lastDay, day)}</h2>
        {count > 0 && <span className="muted">{count === 1 ? '1 thing' : `${count} things`}</span>}
      </div>
      {count > 0 && (
        <div className="since-list">
          {rows.map(({ record, reason }) => {
            const Icon = reason.icon;

            return (
              <button
                key={record.id}
                type="button"
                data-ui="specialized"
                className="choose-row since-row"
                onClick={() => onOpen(record.id)}
              >
                <span className={`since-icon tone-${reason.tone}`}>
                  <Icon aria-hidden="true" />
                </span>
                <span className="since-text">
                  <strong>{(record.value as Task).title}</strong>
                  <small className={`tone-${reason.tone}`}>{reason.label}</small>
                </span>
              </button>
            );
          })}
          {summary.inbox > 0 && (
            <div className="since-row">
              <span className="since-icon tone-info">
                <Inbox aria-hidden="true" />
              </span>
              <span className="since-text">
                <strong>
                  {summary.inbox === 1
                    ? 'One pebble in the Inbox'
                    : `${summary.inbox} pebbles in the Inbox`}
                </strong>
                <small>Sort them whenever you like, or not at all.</small>
              </span>
            </div>
          )}
        </div>
      )}
      <p className="welcome-trail">
        <Footprints aria-hidden="true" />
        {trailWords(summary.quietDays)}
      </p>
      <div className="welcome-actions">
        <Button variant={marked ? 'secondary' : 'primary'} onClick={onSetOut}>
          Choose today’s stones
        </Button>
        <QuietButton
          onClick={onDismiss}
          title="Hides this until you next come back after days away"
        >
          Not now
        </QuietButton>
      </div>
    </section>
  );
}

/** "2 of 4 steps · 1h 29m recorded", with the steps drawn as small stones. */
function Progress({ record, minutes }: { record: LocalRecord; minutes: number }) {
  const task = record.value as Task;
  const done = task.steps.filter(s => s.done).length;

  const parts = [
    ...(task.steps.length ? [`${done} of ${task.steps.length} steps`] : []),
    ...(minutes > 0 ? [`${duration(minutes)} recorded`] : []),
  ];

  if (!parts.length) {
    return null;
  }

  const stone = taskStone(record.id, task);

  return (
    <p className="welcome-progress">
      {task.steps.length > 0 && (
        <span className="step-stones" aria-hidden="true">
          {task.steps
            .slice(0, 8)
            .map(step =>
              step.done ? (
                <StoneMark key={step.id} mineral={stone.mineral} silhouette="e" speckled={false} />
              ) : (
                <GhostStone key={step.id} silhouette="e" />
              ),
            )}
        </span>
      )}
      {parts.join(' · ')}
    </p>
  );
}
